import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/site/Layout";
import { ArrowUpRight, Lock, Sparkles } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1620122303020-87ec826cf70d?w=1800&q=90";

export default function Landing() {
  const [models, setModels] = useState([]);
  const nav = useNavigate();

  useEffect(() => { api.get("/models").then(r => setModels(r.data.slice(0, 4))); }, []);

  return (
    <Layout>
      {/* HERO */}
      <section className="relative min-h-[92vh] overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-40 pb-24">
          <div className="ribbon mb-8 fade-up" data-testid="hero-tagline">Editorial · Est. 2026</div>
          <h1 className="font-serif-editorial text-5xl sm:text-6xl lg:text-8xl leading-[0.9] tracking-tighter max-w-5xl fade-up">
            Blurred until <em className="italic text-neutral-400">earned.</em><br/>
            Unlocked with <span className="italic">intention.</span>
          </h1>
          <p className="mt-10 max-w-xl text-neutral-400 text-base leading-relaxed fade-up">
            A subscription archive of surrealist editorial photography.
            Browse the fog. Spend a credit. Reveal the frame.
          </p>
          <div className="mt-12 flex flex-wrap gap-4 fade-up">
            <button onClick={() => nav("/signup")} className="btn-white" data-testid="hero-cta-signup">
              Begin <ArrowUpRight size={16}/>
            </button>
            <button onClick={() => nav("/models")} className="btn-ghost" data-testid="hero-cta-browse">
              Browse the archive
            </button>
          </div>
        </div>
      </section>

      {/* Featured teasers */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="overline mb-3">Featured / this week</div>
            <h2 className="font-serif-editorial text-4xl md:text-5xl tracking-tighter">Four muses. Blurred.</h2>
          </div>
          <Link to="/models" className="hidden md:inline-flex btn-ghost">View all</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 stagger">
          {models.map((m, i) => {
            const spans = ["md:col-span-7 aspect-[4/5]","md:col-span-5 aspect-[4/5]","md:col-span-5 aspect-[4/5]","md:col-span-7 aspect-[4/5]"];
            return (
              <Link to={`/models/${m.model_id}`} key={m.model_id} className={`relative overflow-hidden group ${spans[i%4]}`} data-testid={`featured-model-${i}`}>
                <img src={m.profile_photo} alt={m.name} className="thumb-locked w-full h-full object-cover"/>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute top-6 left-6"><Lock size={16} className="text-white/70"/></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="overline mb-2">Model / {String(i+1).padStart(2,'0')}</div>
                  <div className="font-serif-editorial text-3xl md:text-4xl tracking-tighter">{m.name}</div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Pricing preview */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 border-t border-white/10">
        <div className="overline mb-3">Membership</div>
        <h2 className="font-serif-editorial text-4xl md:text-5xl tracking-tighter mb-16 max-w-3xl">
          Four tiers. One monochrome obsession.
        </h2>
        <div className="grid md:grid-cols-4 gap-px bg-white/10">
          {[
            {n:"Starter", p:10, c:100}, {n:"Plus", p:30, c:350},
            {n:"Pro", p:45, c:550}, {n:"Elite", p:75, c:1000},
          ].map((t,i) => (
            <div key={t.n} className="bg-black p-8 flex flex-col">
              <div className="overline">Tier {String(i+1).padStart(2,'0')}</div>
              <div className="font-serif-editorial text-3xl mt-3">{t.n}</div>
              <div className="mt-6 font-serif-editorial text-5xl">${t.p}<span className="text-sm text-neutral-500 font-mono-ui">/mo</span></div>
              <div className="mt-2 text-neutral-400 text-sm font-mono-ui">{t.c} credits · monthly</div>
              <Link to="/pricing" className="btn-ghost mt-8" data-testid={`landing-tier-${t.n.toLowerCase()}`}>Choose {t.n}</Link>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonial */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-32 border-t border-white/10">
        <div className="grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-2"><Sparkles size={24} className="text-white"/></div>
          <blockquote className="md:col-span-8 font-serif-editorial text-2xl md:text-4xl leading-tight tracking-tight italic">
            "The most patient photography site on the internet.
            You don't scroll — you approach."
          </blockquote>
          <div className="md:col-span-2 overline">— Fold Magazine</div>
        </div>
      </section>
    </Layout>
  );
}
